import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiAward, FiAlertTriangle, FiBell } from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import apiClient, { endpoints } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCertificates: 0,
    expiringCertificates: 0,
    pendingReminders: 0
  });
  const [expiringData, setExpiringData] = useState([]);
  const [renewalTrendData, setRenewalTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [error, setError] = useState('');
  const { showError } = useToast();

  // Chart colors
  const chartColors = {
    primary: '#FF0404',
    success: '#4caf50',
    warning: '#ff9800'
  };

  const fetchDashboardStats = async () => {
    // Double-check authentication before making API calls
    if (!isAuthenticated()) {
      console.warn('User not authenticated, skipping dashboard stats fetch');
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [usersRes, certificatesRes, remindersRes] = await Promise.all([
        apiClient.get(endpoints.dashboard.users + '?limit=1'),
        apiClient.get(endpoints.dashboard.certificates + '?limit=1'),
        apiClient.get(endpoints.dashboard.reminders + '?limit=1&status=pending')
      ]);

      // Calculate expiring certificates (next 30 days)
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      const expiryEnd = thirtyDaysFromNow.toISOString().split('T')[0];
      
      const expiringRes = await apiClient.get(
        `${endpoints.dashboard.certificates}?limit=1&expiry_end=${expiryEnd}&status=active`
      );

      setStats({
        totalUsers: usersRes.data.data.pagination.totalUsers,
        totalCertificates: certificatesRes.data.data.pagination.totalCertificates,
        expiringCertificates: expiringRes.data.data.pagination.totalCertificates,
        pendingReminders: remindersRes.data.data.pagination.totalReminders
      });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      
      // Handle authentication errors specifically
      if (err.response?.status === 401) {
        console.warn('Authentication failed, redirecting to login');
        navigate('/login');
        return;
      }
      
      showError('Failed to load dashboard statistics');
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartsData = async () => {
    // Double-check authentication before making API calls
    if (!isAuthenticated()) {
      console.warn('User not authenticated, skipping charts data fetch');
      navigate('/login');
      return;
    }

    try {
      setChartsLoading(true);

      // Fetch expiring certificates data
      const expiringResponse = await apiClient.get(endpoints.dashboard.expiring, {
        params: { dateRange: '6months', certificateType: 'all' }
      });

      // Fetch renewal trend data
      const renewalResponse = await apiClient.get(endpoints.dashboard.renewalTrend, {
        params: { dateRange: '6months', certificateType: 'all' }
      });

      setExpiringData(expiringResponse.data);
      setRenewalTrendData(renewalResponse.data);
    } catch (err) {
      console.error('Error fetching charts data:', err);
      
      // Handle authentication errors specifically
      if (err.response?.status === 401) {
        console.warn('Authentication failed, redirecting to login');
        navigate('/login');
        return;
      }
      
      showError('Failed to load charts data');
    } finally {
      setChartsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch data if user is authenticated and auth loading is complete
    if (!authLoading && isAuthenticated()) {
      fetchDashboardStats();
      fetchChartsData();
    } else if (!authLoading && !isAuthenticated()) {
      // If not authenticated, redirect to login
      navigate('/login');
    }
  }, [authLoading, isAuthenticated]);

  const statCards = [
    {
      name: 'Total Clients',
      value: stats.totalUsers,
      icon: FiUsers,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      name: 'Total Certificates',
      value: stats.totalCertificates,
      icon: FiAward,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      name: 'Expiring Soon',
      value: stats.expiringCertificates,
      icon: FiAlertTriangle,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600'
    },
    {
      name: 'Pending Reminders',
      value: stats.pendingReminders,
      icon: FiBell,
      color: 'bg-red-500',
        bgColor: 'bg-red-50',
        textColor: 'text-red-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary btn-pulse"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in page-content">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="mt-1 text-sm text-gray-600">
          Get a snapshot of the system performance, recent activities, and certificate status.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg stagger-item card-hover">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`${stat.bgColor} p-3 rounded-lg`}>
                      <Icon className={`h-6 w-6 ${stat.textColor}`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg animate-slide-up" style={{animationDelay: '0.1s'}}>
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <button 
              onClick={() => navigate('/users')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
            >
              <FiUsers className="-ml-1 mr-2 h-5 w-5" />
              View All Clients
            </button>
            <button 
              onClick={() => navigate('/certificates')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 btn-animate transition-all duration-200"
            >
              <FiAward className="-ml-1 mr-2 h-5 w-5" />
              Manage Certificates
            </button>
            <button 
              onClick={() => navigate('/reminders')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 btn-animate transition-all duration-200"
            >
              <FiBell className="-ml-1 mr-2 h-5 w-5" />
              Send Reminders
            </button>
          </div>
        </div>
      </div>

      {/* Key Reports Charts */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 animate-slide-up" style={{animationDelay: '0.2s'}}>
        {/* Certificates Expiring by Timeframe Chart */}
        <div className="bg-white shadow-lg rounded-lg p-6 transform transition-all duration-300 hover:shadow-xl">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Certificates Expiring by Timeframe</h3>
          {chartsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary btn-pulse"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={expiringData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="timeframe" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }} 
                />
                <Legend />
                <Bar 
                  dataKey="count" 
                  fill={chartColors.primary} 
                  radius={[4, 4, 0, 0]}
                  className="transition-all duration-300 hover:opacity-80"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Renewal Rate Over Time Chart */}
        <div className="bg-white shadow-lg rounded-lg p-6 transform transition-all duration-300 hover:shadow-xl">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Renewal Rate Over Time</h3>
          {chartsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary btn-pulse"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={renewalTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }} 
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="renewals" 
                  stroke={chartColors.success} 
                  strokeWidth={3}
                  dot={{ fill: chartColors.success, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: chartColors.success, strokeWidth: 2 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="expirations" 
                  stroke={chartColors.primary} 
                  strokeWidth={3}
                  dot={{ fill: chartColors.primary, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: chartColors.primary, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* System Status */}
      <div className="mt-8 bg-white shadow-lg rounded-lg transform transition-all duration-300 hover:shadow-xl animate-slide-up" style={{animationDelay: '0.3s'}}>
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            System Status
          </h3>
          <div className="space-y-3">
            <div className="flex items-center transition-all duration-200 hover:bg-gray-50 p-2 rounded">
              <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"></div>
              <span className="text-sm text-gray-600 font-medium">API Server: Online</span>
            </div>
            <div className="flex items-center transition-all duration-200 hover:bg-gray-50 p-2 rounded">
              <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"></div>
              <span className="text-sm text-gray-600 font-medium">Database: Connected</span>
            </div>
            <div className="flex items-center transition-all duration-200 hover:bg-gray-50 p-2 rounded">
              <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"></div>
              <span className="text-sm text-gray-600 font-medium">Email Service: Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;